import React, { useCallback, useEffect, useState } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import { FormProvider, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { FormRow } from '@gravity-ui/components';
import { Button, Link, TextInput } from '@gravity-ui/uikit';
import { zodResolver } from '@hookform/resolvers/zod';

import { useNavigateReplace } from 'hooks';
import { useLoginUserMutation } from 'store/api';
import { routes } from 'routes';

import { EyeButton } from '../../ui-kit';
import {
	ADMINISTRATOR,
	AUTHORIZATION,
	AUTHORIZATION_PROBLEM,
	EMAIL,
	FORGET_PASSWORD,
	LOGIN,
	PASSWORD,
	SUBMIT,
	SUCCESS_AUTH,
} from '../constants';
import type { Login } from '../utils';
import { useValidation } from '../utils';

import styles from './styles.module.scss';

export const LoginAuth = () => {
	const navigate = useNavigateReplace();
	const [hidePass, setHidePass] = useState<boolean>(true);
	const inputType = hidePass ? 'password' : undefined;

	const { loginSchema: schema } = useValidation();

	const methods = useForm<Login>({
		resolver: zodResolver(schema),
		defaultValues: { email: '', password: '' },
	});

	const [loginUser, { isError, error, isSuccess, isLoading }] = useLoginUserMutation();

	const {
		watch,
		register,
		handleSubmit,
		formState: { errors },
	} = methods;

	const formValues = watch();

	// useEffect(() => {
	// 	if (isSuccess) {
	// 		toast.success(SUCCESS_AUTH, { position: 'top-right' });
	// 		navigate(routes.root.getPath());
	// 	}
	// 	if (isError) {
	// 		toast.error((error as any).data.message, {
	// 			position: 'top-right',
	// 		});
	// 	}
	// 	// eslint-disable-next-line react-hooks/exhaustive-deps
	// }, [isLoading]);

	useEffect(() => {
		if (isSuccess) {
			toast.success(SUCCESS_AUTH, { position: 'top-right' });
			navigate(routes.root.getPath());
		}
	}, [isSuccess, navigate]);

	useEffect(() => {
		if (isError && error) {
			const message = (error as any).data?.message || 'Ошибка входа';
			toast.error(message, { position: 'top-right' });
		}
	}, [isError, error]);

	const onSubmitHandler: SubmitHandler<Login> = (values) => {
		loginUser(values);
	};

	const handleClick = useCallback(
		(e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement, MouseEvent>) => {
			e.preventDefault();
			navigate(routes.recovery_pass.path);
		},
		[navigate],
	);

	return (
		<div className={styles.login_auth}>
			<h1>{AUTHORIZATION}</h1>

			<FormProvider {...methods}>
				<form className={styles.login_auth__form}>
					<div className={styles.login_auth__form__group}>
						<FormRow label={LOGIN} direction="column">
							<TextInput
								size="xl"
								{...register('email')}
								error={errors.email?.message || isError}
								value={formValues.email}
							/>
						</FormRow>
					</div>

					<div className={styles.login_auth__form__group}>
						<FormRow label={PASSWORD} direction="column">
							<TextInput
								size="xl"
								{...register('password')}
								value={formValues.password}
								error={errors.password?.message || isError}
								type={inputType}
								endContent={<EyeButton opened={hidePass} onClick={() => setHidePass(!hidePass)} />}
							/>
						</FormRow>
					</div>

					<Button
						onClick={handleSubmit(onSubmitHandler)}
						loading={isLoading}
						type="submit"
						size="xl"
						className={styles.login_auth__form__btn}
					>
						{SUBMIT}
					</Button>
				</form>
			</FormProvider>

			<div className={styles.login_auth__help}>
				<Button size="xl" view="flat" className={styles.login_auth__help__link} onClick={handleClick}>
					{FORGET_PASSWORD}
				</Button>

				<span className={styles.login_auth__help__info}>{AUTHORIZATION_PROBLEM}</span>
				<span className={styles.login_auth__help__info}>
					{ADMINISTRATOR}
					<Link view="normal" href="mailto:edu@fond-igra.ru">
						{EMAIL}
					</Link>
				</span>
			</div>
		</div>
	);
};
